import React, { memo, useEffect, useId, useState } from "react";
import {
  ReactNode,
  useLayoutEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { testIds } from "src/test/testIds";
import Map from "ol/Map";
import View from "ol/View";
import {
  Control,
  ScaleLine,
  Zoom,
  defaults as defaultControls,
} from "ol/control";
import { fromLonLat } from "ol/proj";
import TileLayer from "ol/layer/Tile";
import { OSM } from "ol/source";
import { MapContext } from "./MapContext";
import { VectorSourceProvider } from "./hooks/incontext/useVectorSourceContext";
import { useHoverCursor } from "./hooks/incontext/useHoverCursor";
import { useLazyRef } from "./hooks/useLazyRef";
import { moveViewCenter, prefersReducedMotion } from "./utils/viewCenter";
import { boundingExtent } from "ol/extent";
// CSS import는 사용자가 직접 해야 합니다: import "ol/ol.css";
import DoubleClickZoom from "ol/interaction/DoubleClickZoom";

export type Lng = number;
export type Lat = number;

export type Location = [Lng, Lat];

/**
 * `MapContainer` props — 2D 지도 컴포넌트의 진입 prop 셋.
 * 좌표는 항상 `[lng, lat]` 순서 (WGS84). `bounds` 가 있으면 `center` 보다 우선한다.
 */
export interface MapProps {
  scrollWheelZoom?: boolean;

  /**
   * @default 24
   */
  maxZoom?: number;

  /**
   * @default 3
   */
  minZoom?: number;
  fullscreenControl?: boolean;

  /**
   * @default true.
   */
  isZoomAbled?: boolean;

  /**
   * @default true.
   */
  isRotateAbled?: boolean;

  /**
   * @default [127.9745613, 37.3236563]
   */
  center?: Location;

  /**
   * @default 15
   */
  zoomLevel?: number;

  /**
   * @default null
   * @description [[minX, minY], [maxX, maxY]]
   */
  bounds?: [Location, Location];

  /**
   * @default "1000px"
   */
  height?: string;

  /**
   * @default "1000px"
   */
  width?: string;

  /**
   * @default true
   */
  isShownOsm?: boolean;

  /**
   * @default false
   * @description If you set this property to 'true', you can see selection of annotations.
   */
  hasScaleLine?: boolean;

  /**
   * 렌더 픽셀 배율. 미지정 시 OpenLayers 기본값(`window.devicePixelRatio`)을 쓴다.
   *
   * `1` 로 고정하면 레이어 캔버스가 CSS 픽셀과 1:1 이 된다 — HiDPI 화면에서 캔버스 면적과
   * 타일 요청 수가 DPR² 배로 부풀지 않는다. 화면에 보여줄 지도는 기본값이 맞고, 출력 크기가
   * 이미 정해진 오프스크린 렌더(지도 캡쳐 등)에서만 `1` 을 쓴다.
   *
   * ⚠️ OpenLayers 의 `Map` 생성자 옵션이라 **마운트 시 한 번만** 반영된다. 이후 변경은 무시된다.
   */
  pixelRatio?: number;

  /**
   * `center` 가 바뀔 때 새 중심까지 부드럽게 이동하는 시간(ms). 미지정이면 즉시 옮긴다.
   * 움직임 줄이기(`prefers-reduced-motion: reduce`) 환경에서는 값이 있어도 즉시 옮긴다.
   */
  centerAnimationMs?: number;
  children?: ReactNode;
}

export const MapContainer = memo(
  forwardRef<Map, MapProps>(
    (
      {
        children,
        isZoomAbled = true,
        isRotateAbled = false,
        center = [126.841, 35.1896563],
        zoomLevel = 18,
        bounds,
        maxZoom = 24,
        minZoom = 3,
        height = "1000px",
        width = "1000px",
        isShownOsm = true,
        hasScaleLine = true,
        pixelRatio,
        centerAnimationMs,
      },
      ref
    ) => {
      const id = useId();
      const mapId = `react-openlayers-map-${id}`;
      const [isReady, setIsReady] = useState(false);

      // useRef(new Heavy()) 는 매 렌더마다 throwaway 인스턴스를 생성한다 — lazy-init 으로 최초 1회만 생성(D7).
      const osmRef = useLazyRef<TileLayer<OSM>>(
        () =>
          new TileLayer({
            zIndex: -1,
            source: new OSM({
              crossOrigin: "anonymous",
              attributions: [],
            }),
          })
      );

      const mapObj = useLazyRef<Map>(
        () =>
          new Map({
            // OL 생성자 옵션 — 마운트 시 1회만 반영된다(prop 변경은 무시).
            ...(pixelRatio !== undefined && { pixelRatio }),
            view: new View({
              zoom: zoomLevel,
            }),
            controls: defaultControls({
              zoom: isZoomAbled,
              rotate: isRotateAbled,
            }).extend(
              hasScaleLine
                ? [
                    new ScaleLine({
                      units: "metric", // 미터 단위 사용
                      steps: 4, // 스케일 단계 수
                      text: true, // 텍스트 표시
                      minWidth: 64, // 최소 너비
                    }),
                  ]
                : []
            ),
          })
      );

      useEffect(() => {
        if (isShownOsm) {
          mapObj.current.addLayer(osmRef.current);
        } else {
          mapObj.current.removeLayer(osmRef.current);
        }
      }, [isShownOsm]);

      useEffect(() => {
        if (mapObj.current) {
          const view = mapObj.current.getView();
          view.setMinZoom(!isZoomAbled ? zoomLevel : minZoom);
        }
      }, [isZoomAbled, minZoom, zoomLevel]);

      useEffect(() => {
        if (mapObj.current) {
          const view = mapObj.current.getView();
          view.setMaxZoom(!isZoomAbled ? zoomLevel : maxZoom);
        }
      }, [maxZoom, isZoomAbled, zoomLevel]);

      useEffect(() => {
        if (mapObj.current) {
          const view = mapObj.current.getView();
          view.setZoom(zoomLevel);
        }
      }, [zoomLevel]);

      useEffect(() => {
        if (mapObj.current && center) {
          moveViewCenter(
            mapObj.current.getView(),
            fromLonLat(center),
            centerAnimationMs,
            prefersReducedMotion()
          );
        }
      }, [center?.[0], center?.[1]]);

      useEffect(() => {
        if (mapObj.current && bounds) {
          const view = mapObj.current.getView();

          view.fit(
            boundingExtent([fromLonLat(bounds[0]), fromLonLat(bounds[1])]),
            {
              padding: [20, 20, 20, 20],
            }
          );
        }
      }, [
        bounds?.[0]?.[0],
        bounds?.[0]?.[1],
        bounds?.[1]?.[0],
        bounds?.[1]?.[1],
      ]);

      useHoverCursor(mapObj.current);

      // target 컨테이너 크기 변화 시 맵이 새 크기를 반영해 타일을 다시 표시하도록 보장하는 안전망(D1).
      useEffect(() => {
        if (!isReady) return;
        const map = mapObj.current;
        const target = map.getTargetElement();
        if (!target) return;
        const resizeObserver = new ResizeObserver(() => {
          map.updateSize();
        });
        resizeObserver.observe(target);
        return () => resizeObserver.disconnect();
      }, [isReady]);

      useImperativeHandle(ref, () => mapObj.current);

      useLayoutEffect(() => {
        const mapRef = mapObj.current;
        mapRef.getInteractions().forEach((interaction) => {
          if (interaction instanceof DoubleClickZoom) {
            interaction.setActive(false);
          }
        });
        const defaultZoomControl = mapRef
          .getControls()
          .getArray()
          .find((control: Control) => control instanceof Zoom);

        if (defaultZoomControl) {
          mapRef.removeControl(defaultZoomControl);
        }
        mapRef.setTarget(mapId);
        setIsReady(true);
        return () => {
          mapRef.dispose();
          mapRef.setTarget(undefined);
          mapRef.setLayers([]);
        };
      }, [mapId]);

      // MapContext.Provider 에 객체 저장
      return (
        <VectorSourceProvider>
          <MapContext.Provider value={mapObj.current}>
            <div
              id={mapId}
              className="react-openlayers-map-container"
              data-testid={testIds.map.container2d}
              style={{ width, height }}
            >
              {isReady && children}
            </div>
          </MapContext.Provider>
        </VectorSourceProvider>
      );
    }
  )
);
